<template>
  <div class="p-4 relative">
    <PlanOverlay
      :visible="ideasPerm == 'none'"
      :reason="ideasPerm == 'none' && isSubUser ? 'permission' : 'plan'"
    />
    <!-- Barre de recherche -->
    <div class="relative mb-4">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Rechercher une idée..."
        class="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:border-blue-300"
      />
      <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
        <i class="fas fa-search"></i>
      </span>
      <span
        v-if="searchQuery.trim().length"
        class="cursor-pointer absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
        @click="searchQuery = ''"
      >
        <i class="fas fa-times"></i>
      </span>
    </div>
    <div class="w-fit h-fit flex items-center mb-8 gap-2">
      <span>Trier par :</span>
      <select class="basicInput w-fit" value="1" v-model="selectedSort">
        <option value="1">Date d'ajout (Croissant)</option>
        <option value="0">Date d'ajout (Décroissant)</option>
        <option value="3">Priorité (Croissant)</option>
        <option value="2">Priorité (Décroissant)</option>
      </select>
    </div>
    <div class="w-4/5 h-fit flex flex-col relative">
      <div
        v-if="ideasPerm != 'modification'"
        style="position:absolute;top:0;left:0;width:100%;height:100%;z-index:1000;cursor:not-allowed;background:rgba(255,255,255,0.01);"
        @mousedown.stop
        @mouseup.stop
        @click.stop
        @pointerdown.stop
        @pointerup.stop
        @touchstart.stop
        @touchend.stop
      ></div>
      <div v-for="idea in sortedIdeas" :key="idea.id" class="w-full h-fit flex gap-4">
        <div class="w-4/5">
          <div class="w-full flex flex-col gap-4">
            <div class="w-full flex items-center gap-4">
              <input type="text" v-model="idea.title" @input="updateIdea(idea)" placeholder="Nommez votre idée" class="w-full basicInput" ref="ideaTitle">
              <select value=0 v-model="idea.priority" @change="updateIdea(idea)" class="basicInput w-fit" :style="{ color: priorities.find(p => p.id === idea.priority).color || '#222' }">
                <option v-for="priority in priorities" :key="priority.id" :value="priority.id" :style="{ color: priority.color }">
                  {{ priority.name }}
                </option>
              </select>
            </div>
            <textarea v-model="idea.description" @input="updateIdea(idea)" placeholder="Décrivez votre idée" class="w-full basicInput !h-[120px] resize-none !placeholder-gray-400" ref="ideaDescription"></textarea>
          </div>
          <div class="w-full border border-gray-300 mt-4 mb-4"></div>
        </div>
        <div class="w-1/5 flex flex-col gap-4">
          <button class="callToAction w-full" @click="addNewProject(idea); deleteIdea(idea)" v-if="perm().projects == 'modification'">Créer un projet</button>
          <button class="callToAction w-full" @click="addNewEvent(idea); deleteIdea(idea)" v-if="perm().planning == 'modification'">Créer un évènement</button>
          <button class="w-full flex items-center justify-center" @click="deleteIdea(idea)" v-if="ideasPerm == 'modification'">Supprimer</button>
        </div>
      </div>
      <div class="w-4/5 h-fit flex gap-4">
        <div class="w-full pr-3">
          <div class="w-full flex flex-col gap-4">
            <input type="text" placeholder="Nommez votre idée" class="w-full basicInput" @click="addIdea(false)">
            <textarea placeholder="Décrivez votre idée" class="w-full basicInput !h-[120px] resize-none !placeholder-gray-400" @click="addIdea(true)"></textarea>
          </div>
        </div>
      </div>
    </div>
    <PopupEvent
      :event="popupEvent"
      :visible="popupEventVisible"
      :byIdea="true"
      @close="popupEventVisible = false"
    />
  </div>
</template>

<script>
import { fetchIdeas, deleteIdea, addIdea, updateIdea } from '@/services/ideaService.js';
import { addProject, updateProject } from '@/services/projectService';

import PopupEvent from '@/components/Popups/PopupEvent.vue';
import PlanOverlay from '@/components/Others/PlanOverlay.vue';

import { inject } from 'vue';

export default {
  name: 'Ideas',
  components: {
    PopupEvent,
    PlanOverlay,
  },
  data() {
    return {
      ideas: [],
      popupEventVisible: false,
      popupEvent: {
        ressources: [],
        startDate: new Date(),
        endDate: new Date(),
        title: "",
        projectId: "",
        isFinished: false,
        color: "#E8535D",
        tasks: [],
      },
      priorities: [
        { id: 0, name: "Aucune priorité", color: "#000000" },
        { id: 1, name: "Priorité Faible", color: "#00FF00" },
        { id: 2, name: "Priorité Moyenne", color: "#f59e42" },
        { id: 3, name: "Urgent", color: "#FF0000" },
      ],
      selectedSort: 0,
      searchQuery: "",
      planFeatures: {
        projects: false, // This should be dynamically set based on the user's plan
      },
    };
  },
  setup() {
    const perm = inject('userPerm');
    const currentUser = inject('currentUser');
    return { perm, currentUser };
  },
  computed: {
    ideasPerm() {
      console.log("ideasPerm", this.perm());
      return this.perm().ideas;
    },
    isSubUser() {
      const user = this.currentUser && this.currentUser();
      return user ? !!user.mainUserId : false;
    },
    sortedIdeas() {
      let result = this.ideas;
      // Filtre par recherche (full name)
      if (this.searchQuery.trim()) {
        const search = this.searchQuery.toLowerCase();
        result = result.filter(res =>
          (`${res.title || ''} ${res.description || ''}`).toLowerCase().includes(search)
        );
      }

      return result.sort((a, b) => {
        switch (this.selectedSort) {
          case "0":
            return b.order - a.order;
          case "1":
            return a.order - b.order;
          case "2":
            return b.priority - a.priority;
          case "3":
            return a.priority - b.priority;
          default:
            return 0;
        }
      });
    },
  },
  methods:{
    deleteIdea(idea) {
      console.log("delete");
      this.ideas.splice(this.ideas.findIndex(i => i.id === idea.id), 1);
      deleteIdea(idea.id);
    },
    updateIdea(idea) {
      updateIdea(idea);
    },
    async addNewProject(idea) {
      const project = await addProject();
      project.name = idea.title;
      project.description = idea.description;
      await updateProject(project);
      this.$router.push({ path: `/Projet/${project.id}` });
    },
    addNewEvent(idea) {
      console.log(idea);
      this.popupEvent.title = idea.title;
      this.popupEvent.tasks = [{ id: 0, content: idea.description, isFinished: false, subTasks: [] }];
      this.popupEventVisible = true;
    },
    async addIdea(focusDescription) {
      this.searchQuery = '';
      const newIdea = await addIdea({ title: '', description: '', priority: 0 });
      this.ideas.push(newIdea);
      this.$nextTick(() => {
        // Utilise la référence du textarea et place le focus sur le dernier élément
        if(focusDescription) {
          const refs = this.$refs.ideaDescription;
          const newIdeaField = Array.isArray(refs) ? refs[refs.length - 1] : refs;
          if(newIdeaField) {
            newIdeaField.focus();
          }
        }
        else {
          const refs = this.$refs.ideaTitle;
          const newIdeaField = Array.isArray(refs) ? refs[refs.length - 1] : refs;
          if(newIdeaField) {
            newIdeaField.focus();
          }
        }
      });
    },
  },
  async mounted() {
    this.ideas = await fetchIdeas();
  },
};
</script>